import { Store } from 'vuex'

import { SuggestionJustification } from '@/misc/types'
import { Chunk, Report, TextualChunkOrigin, TextualHealthEntry } from '@/stay-displayer/health-data/types'
import { CodingSelection, SelectedChunks } from '@/stay-displayer/types'
import { RootState } from '@/store/types'

type ChunkModifier = {
  chunkClass: string
  modifiers: {
    clickable: boolean
    highlighted: boolean
  }
}

// Priority mirrors the backend chunker's own color computation (isNegative > isDoubt >
// isFalseJustification), plus a hasSearchMatch/origins split for the remaining "highlighted" case:
// a plain saved-search match reads as a keyword hit, a prediction with no textual match as a hint.
function getChunkModifiers(chunk: Chunk, selection: CodingSelection | null): ChunkModifier {
  const origins = chunk.origins ?? []
  const modifiers = { clickable: false, highlighted: false }
  let chunkClass = ''

  if (chunk.isNegative) {
    chunkClass = 'health-data-negation'
  } else if (chunk.isDoubt) {
    chunkClass = 'health-data-doubt'
  } else if (chunk.isFalseJustification) {
    chunkClass = 'health-data-reported'
    modifiers.clickable = true
  } else if (chunk.hasSearchMatch) {
    chunkClass = 'health-data-keyword'
    modifiers.clickable = true
  } else if (origins.length > 0) {
    chunkClass = `health-data-hint-${origins[0].labelType}`
    modifiers.clickable = true
  }

  if (selection && origins.some((origin) => origin.labelType === selection.labelType && origin.labelReference === selection.labelReference)) {
    modifiers.highlighted = true
  }

  return { chunkClass, modifiers }
}

export function getChunkClasses(chunk: Chunk, selection: CodingSelection | null): string[] {
  const { chunkClass, modifiers } = getChunkModifiers(chunk, selection)

  const addedClasses = Object.keys(modifiers).filter((key) => modifiers[key])

  return [chunkClass + (addedClasses.length ? `__${addedClasses.sort().join('-')}` : '')]
}

export function chunkIsSelected(chunk: Chunk, selection: CodingSelection | null): boolean {
  return getChunkModifiers(chunk, selection).modifiers.highlighted
}

const STRUCTURED_SOURCE_TYPE_TO_CHUNK_KEY: Record<string, 'drugEvents' | 'healthConstants' | 'labResults' | 'categoricalLabResults'> = {
  drugEvent: 'drugEvents',
  healthConstant: 'healthConstants',
  labResult: 'labResults',
  categoricalLabResult: 'categoricalLabResults',
}

const TEXTUAL_SOURCE_TYPE_TO_CHUNK_KEY: Record<string, 'reports' | 'textualHealthEntries'> = {
  report: 'reports',
  textualHealthEntry: 'textualHealthEntries',
}

// The backend already resolves exactly which drugEvent/healthConstant/labResult/categoricalLabResult
// row and which report/textualHealthEntry (same StructuredJustificationItem/TextualJustificationItem
// data ApiJustificationSummary.vue uses for the justification popin) justify a suggestion — reused
// here to highlight those same rows/documents in the document/health-data viewer (ReportSelector.vue,
// DocumentViewer.vue). textualJustifications carry the document's generatedDataId, resolved back to
// the id the viewer keys on.
export function getSelectedChunks(justification: SuggestionJustification | null, documentIdByGeneratedDataId: Map<string, number>): SelectedChunks {
  const chunks: SelectedChunks = { reports: {}, textualHealthEntries: {}, drugEvents: {}, healthConstants: {}, labResults: {}, categoricalLabResults: {} }

  for (const { sourceType, sourceId } of justification?.structuredJustifications ?? []) {
    const key = STRUCTURED_SOURCE_TYPE_TO_CHUNK_KEY[sourceType]
    if (key) {
      chunks[key][sourceId] = true
    }
  }

  for (const { sourceType, sourceId } of justification?.textualJustifications ?? []) {
    const key = TEXTUAL_SOURCE_TYPE_TO_CHUNK_KEY[sourceType]
    const documentId = documentIdByGeneratedDataId.get(sourceId)
    if (key && documentId !== undefined) {
      chunks[key][documentId] = true
    }
  }

  return chunks
}

const ERROR_TYPES: [string, string][] = [
  ['negation', 'Négation'],
  ['antecedent', 'Antécédent'],
  ['doubt', 'Passage incertain'],
  ['recurrence', 'Motif récurrent'],
]

export type FlagOption = {
  key: string
  label: string
  errorType: string
  origin: TextualChunkOrigin
}

// A chunk can justify more than one suggestion at once (e.g. two overlapping diagnoses) — in that
// case each option is labelled with its origin so the "signaler comme" menu stays unambiguous about
// which suggestion the flag applies to; the common single-origin case keeps the plain error-type label.
export function getFlagOptions(chunk: Chunk): FlagOption[] {
  const origins = chunk.origins ?? []
  const multiple = origins.length > 1

  return origins.flatMap((origin) => ERROR_TYPES.map(([errorType, errorLabel]) => ({
    key: `${origin.labelType}:${origin.labelReference}:${errorType}`,
    label: multiple ? `${origin.labelType} ${origin.labelReference} — ${errorLabel}` : errorLabel,
    errorType,
    origin,
  })))
}

export function tagAsFalseJustification(report: Report | null, healthEntry: TextualHealthEntry | null, chunk: Chunk, errorType: string, origin: TextualChunkOrigin, store: Store<RootState>): boolean {
  store.dispatch('createFalseJustificationForSuggestion', {
    errorType,
    docType: report ? 'report' : 'textualHealthEntry',
    docId: report ? report.id : healthEntry.id,
    hintStart: chunk.chunkStart,
    hintEnd: chunk.chunkEnd,
    labelType: origin.labelType,
    labelReference: origin.labelReference,
  })

  return false
}

export function tagAsLegitJustification(report: Report | null, healthEntry: TextualHealthEntry | null, chunk: Chunk, store: Store<RootState>): boolean {
  const currentDoc = report || healthEntry
  const justifications = (currentDoc.falseJustifications ?? []).filter((j) => j.hintStart === chunk.chunkStart && j.hintEnd === chunk.chunkEnd)
  justifications.forEach((j) => {
    store.dispatch('deleteFalseJustification', {
      justificationId: j.id,
      reportId: report ? report.id : null,
      healthEntryId: healthEntry ? healthEntry.id : null,
    })
  })

  return false
}

export function toggleFalseJustificationTypes(chunk: Chunk, currentStatus: { [hash: string]: boolean }): void {
  if (chunk.isHighlighted) {
    currentStatus[chunk.hash] = !currentStatus[chunk.hash]
  }
}
